/**
 * TrackEditor - Interactive tool for editing track points (inserting/deleting frames).
 * Allows users to add interpolated points to tracks when there are data gaps.
 * Uses map click handler and vessel selection UI similar to CourseCreator.
 */
import maplibregl from 'maplibre-gl';
import { MapboxOverlay } from '@deck.gl/mapbox';
import * as bd from '../domain/boat-data.js';
import * as util from '../utils/utils.js';
import plusIconUrl from '../../icons/plus.svg?url';
import deleteIconUrl from '../../icons/delete.svg?url';

export class TrackEditor {
  constructor(map, sail, onTracksModifiedFn = null, onSaveFn = null, onActivateFn = null, onDeactivateFn = null) {
    this._map = map;
    this._sail = sail;
    this._onTracksModifiedFn = onTracksModifiedFn;
    this._onSaveFn = onSaveFn;
    this._onActivateFn = onActivateFn;
    this._onDeactivateFn = onDeactivateFn;
    this._active = false;
    this._pendingAddTrack = null;
    this._pendingAddTrackIdx = null;
    this._clickHandler = null;
    this._overlay = null;
    this._addedPoints = [];
    this._markers = [];
    this._changeLog = [];
    this._deleteMarker = null;
  }

  activate() {
    if (this._active) return;
    this._active = true;

    if (this._onActivateFn) {
      this._onActivateFn();
    }

    this._ensureOverlay();
    this._showDrawer(true);
    this._populateVesselList();

    this._clickHandler = (e) => this._handleMapClick(e);
    this._map.on('click', this._clickHandler);

    this._updateCursor();
  }

  deactivate() {
    if (!this._active) return;
    this._active = false;

    if (this._clickHandler) {
      this._map.off('click', this._clickHandler);
      this._clickHandler = null;
    }

    if (this._overlay) {
      this._map.removeControl(this._overlay);
      this._overlay = null;
    }

    this._showDrawer(false);
    this._pendingAddTrack = null;
    this._pendingAddTrackIdx = null;
    this._addedPoints = [];
    this._markers.forEach(marker => marker.remove());
    this._markers = [];
    if (this._deleteMarker) {
      this._deleteMarker.remove();
      this._deleteMarker = null;
    }
    this._changeLog = [];
    this._clearHighlight();
    this._updateCursor();

    if (this._onDeactivateFn) {
      this._onDeactivateFn();
    }
  }

  isActive() {
    return this._active;
  }

  _ensureOverlay() {
    if (this._overlay) return;
    this._overlay = new MapboxOverlay({ layers: [] });
    this._map.addControl(this._overlay);
  }

  _showDrawer(visible) {
    const wrapper = document.getElementById('trackEditorWrapper');
    const drawer = document.getElementById('trackEditorDrawer');
    if (wrapper) wrapper.style.display = visible ? 'flex' : 'none';
    if (drawer) drawer.style.display = visible ? 'flex' : 'none';
  }

  _confirmClose() {
    if (this._changeLog.length > 0) {
      const dialog = document.createElement('div');
      dialog.className = 'modal-dialog';
      dialog.style.display = 'flex';

      const backdrop = document.createElement('div');
      backdrop.className = 'modal-backdrop';
      dialog.appendChild(backdrop);

      const content = document.createElement('div');
      content.className = 'modal-content';
      content.innerHTML = `
        <h3>Close Without Saving?</h3>
        <p>You have ${this._changeLog.length} unsaved change${this._changeLog.length > 1 ? 's' : ''}. Close without saving?</p>
        <div class="modal-buttons">
          <button id="closeWithoutSaveOK" class="primary-button">Close</button>
          <button id="closeWithoutSaveCancel" class="secondary-button">Cancel</button>
        </div>
      `;
      dialog.appendChild(content);

      document.body.appendChild(dialog);

      document.getElementById('closeWithoutSaveOK').addEventListener('click', () => {
        dialog.remove();
        this._changeLog = []; // Clear changes so we don't prompt again
        this.deactivate();
      });

      const cancelHandler = () => {
        dialog.remove();
      };

      document.getElementById('closeWithoutSaveCancel').addEventListener('click', cancelHandler);
      backdrop.addEventListener('click', cancelHandler);
    } else {
      this.deactivate();
    }
  }

  hasUnsavedChanges() {
    return this._changeLog.length > 0;
  }

  _populateVesselList() {
    const container = document.getElementById('trackEditorVesselList');
    if (!container) {
      console.error('TrackEditor: trackEditorVesselList container not found');
      return;
    }
    container.innerHTML = '';

    const tracks = this._sail.getTracks();
    console.log('TrackEditor: populating vessel list with', tracks.length, 'tracks');
    tracks.forEach((track, idx) => {
      const row = document.createElement('div');
      row.style.display = 'flex';
      row.style.alignItems = 'center';
      row.style.gap = '8px';
      row.style.padding = '4px 0';

      const nameLabel = document.createElement('span');
      nameLabel.id = `track-editor-name-${idx}`;
      nameLabel.textContent = track.getShipName();
      nameLabel.style.flex = '1';
      row.appendChild(nameLabel);

      const addButton = document.createElement('button');
      addButton.className = 'icon-button';
      addButton.title = 'Add point to track';
      addButton.innerHTML = `<img src="${plusIconUrl}" style="width: 16px; height: 16px;"/>`;
      addButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this._startAddPoint(track, idx);
      });
      row.appendChild(addButton);

      const deleteButton = document.createElement('button');
      deleteButton.className = 'icon-button';
      deleteButton.title = 'Delete the current location from track';
      deleteButton.innerHTML = `<img src="${deleteIconUrl}" style="width: 16px; height: 16px;"/>`;
      deleteButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this._confirmDeletePoint(track, idx);
      });
      row.appendChild(deleteButton);

      container.appendChild(row);
      console.log('TrackEditor: added vessel', track.getShipName());
    });
  }

  _startAddPoint(track, trackIdx) {
    this._pendingAddTrack = track;
    this._pendingAddTrackIdx = trackIdx;
    this._highlightVessel(trackIdx);
    this._updateCursor();
    util.showToast('Click on map to add point');
  }

  _addPointToTrack(track, point) {
    const frames = track.getFrames();
    if (!frames || frames.length === 0) return;

    const playTsSec = track.getPlayTsSec();
    const playIdx = track.getPlayIndex();
    
    const newFrame = bd.createFrame(playTsSec, point.lat, point.lon);
    frames.splice(playIdx, 0, newFrame);

    this._changeLog.push(`Added point to ${track.getShipName()} at ${util.formatDate(newFrame.t)}`);

    // Clear state
    this._clearHighlight();
    this._pendingAddTrack = null;
    this._pendingAddTrackIdx = null;
    this._addedPoints = [];
    this._markers.forEach(marker => marker.remove());
    this._markers = [];
    this._updateCursor();

    if (this._onTracksModifiedFn) {
      this._onTracksModifiedFn();
    }
  }

  _confirmDeletePoint(track, trackIdx) {
    const frames = track.getFrames();
    if (!frames || frames.length === 0) return;

    const playIdx = track.getPlayIndex();
    if (playIdx < 0 || playIdx >= frames.length) return;

    const frame = frames[playIdx];
    
    // Show red circle at point location
    const circleElement = this._createCircleElement('rgba(255, 0, 0, 0.31)');
    this._deleteMarker = new maplibregl.Marker({ element: circleElement })
      .setLngLat([frame.lon, frame.lat])
      .addTo(this._map);

    // Show confirmation dialog
    const dialog = document.createElement('div');
    dialog.className = 'modal-dialog';
    dialog.style.display = 'flex';

    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    dialog.appendChild(backdrop);

    const content = document.createElement('div');
    content.className = 'modal-content';
    content.innerHTML = `
      <h3>Delete Point</h3>
      <p>Delete highlighted point from ${track.getShipName()}?</p>
      <div class="modal-buttons">
        <button id="deletePointOK" class="primary-button">Delete</button>
        <button id="deletePointCancel" class="secondary-button">Cancel</button>
      </div>
    `;
    dialog.appendChild(content);

    document.body.appendChild(dialog);

    document.getElementById('deletePointOK').addEventListener('click', () => {
      dialog.remove();
      if (this._deleteMarker) {
        this._deleteMarker.remove();
        this._deleteMarker = null;
      }
      this._deletePointFromTrack(track);
    });

    const cancelHandler = () => {
      dialog.remove();
      if (this._deleteMarker) {
        this._deleteMarker.remove();
        this._deleteMarker = null;
      }
    };

    document.getElementById('deletePointCancel').addEventListener('click', cancelHandler);
    backdrop.addEventListener('click', cancelHandler);
  }

  _deletePointFromTrack(track) {
    const frames = track.getFrames();
    if (!frames || frames.length === 0) return;

    const playIdx = track.getPlayIndex();
    if (playIdx < 0 || playIdx >= frames.length) return;

    const deletedFrame = frames[playIdx];
    frames.splice(playIdx, 1);

    this._changeLog.push(`Deleted point from ${track.getShipName()} at ${util.formatDate(deletedFrame.t)}`);

    if (this._onTracksModifiedFn) {
      this._onTracksModifiedFn();
    }
  }

  _confirmCutStart() {
    const tracks = this._sail.getTracks();
    if (!tracks || tracks.length === 0) return;

    const firstTrack = tracks[0];
    const playTsSec = firstTrack.getPlayTsSec();
    
    const dialog = document.createElement('div');
    dialog.className = 'modal-dialog';
    dialog.style.display = 'flex';

    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    dialog.appendChild(backdrop);

    const content = document.createElement('div');
    content.className = 'modal-content';
    content.innerHTML = `
      <h3>Cut Frames Before Current Time?</h3>
      <p>This will remove all frames before ${util.formatDate(playTsSec * 1000)} from all vessels.</p>
      <div class="modal-buttons">
        <button id="cutStartOK" class="primary-button">Cut</button>
        <button id="cutStartCancel" class="secondary-button">Cancel</button>
      </div>
    `;
    dialog.appendChild(content);

    document.body.appendChild(dialog);

    document.getElementById('cutStartOK').addEventListener('click', () => {
      dialog.remove();
      this._cutFramesBefore(playTsSec);
    });

    const cancelHandler = () => {
      dialog.remove();
    };

    document.getElementById('cutStartCancel').addEventListener('click', cancelHandler);
    backdrop.addEventListener('click', cancelHandler);
  }

  _confirmCutEnd() {
    const tracks = this._sail.getTracks();
    if (!tracks || tracks.length === 0) return;

    const firstTrack = tracks[0];
    const playTsSec = firstTrack.getPlayTsSec();
    
    const dialog = document.createElement('div');
    dialog.className = 'modal-dialog';
    dialog.style.display = 'flex';

    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    dialog.appendChild(backdrop);

    const content = document.createElement('div');
    content.className = 'modal-content';
    content.innerHTML = `
      <h3>Cut Frames After Current Time?</h3>
      <p>This will remove all frames after ${util.formatDate(playTsSec * 1000)} from all vessels.</p>
      <div class="modal-buttons">
        <button id="cutEndOK" class="primary-button">Cut</button>
        <button id="cutEndCancel" class="secondary-button">Cancel</button>
      </div>
    `;
    dialog.appendChild(content);

    document.body.appendChild(dialog);

    document.getElementById('cutEndOK').addEventListener('click', () => {
      dialog.remove();
      this._cutFramesAfter(playTsSec);
    });

    const cancelHandler = () => {
      dialog.remove();
    };

    document.getElementById('cutEndCancel').addEventListener('click', cancelHandler);
    backdrop.addEventListener('click', cancelHandler);
  }

  _cutFramesBefore(cutTimeSec) {
    const tracks = this._sail.getTracks();

    tracks.forEach(track => {
      const frames = track.getFrames();
      if (!frames || frames.length === 0) return;

      const cutIndex = track.getFrameIndexAtTs(cutTimeSec);
      if (cutIndex > 0) {
        frames.splice(0, cutIndex);
      }
    });

    this._changeLog.push(`Cut frames before ${util.formatDate(cutTimeSec * 1000)}`);
    util.showToast(`Removed frames`);

    if (this._onTracksModifiedFn) {
      this._onTracksModifiedFn();
    }
  }

  _cutFramesAfter(cutTimeSec) {
    const tracks = this._sail.getTracks();

    tracks.forEach(track => {
      const frames = track.getFrames();
      if (!frames || frames.length === 0) return;

      const cutIndex = track.getFrameIndexAtTs(cutTimeSec);
      if (cutIndex >= 0 && cutIndex < frames.length - 1) {
        frames.splice(cutIndex + 1);
      }
    });

    this._changeLog.push(`Cut frames after ${util.formatDate(cutTimeSec * 1000)}`);
    util.showToast(`Removed frames`);

    if (this._onTracksModifiedFn) {
      this._onTracksModifiedFn();
    }
  }

  _highlightVessel(trackIdx) {
    this._clearHighlight();
    const nameLabel = document.getElementById(`track-editor-name-${trackIdx}`);
    if (nameLabel) {
      nameLabel.style.backgroundColor = 'rgba(255, 255, 0, 0.3)';
      nameLabel.style.padding = '2px 4px';
      nameLabel.style.borderRadius = '2px';
    }
  }

  _clearHighlight() {
    const allLabels = document.querySelectorAll('[id^="track-editor-name-"]');
    allLabels.forEach(label => {
      label.style.backgroundColor = '';
      label.style.padding = '';
      label.style.borderRadius = '';
    });
  }

  _showSaveDialog() {
    const dialog = document.createElement('div');
    dialog.className = 'modal-dialog';
    dialog.style.display = 'flex';

    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    dialog.appendChild(backdrop);

    const content = document.createElement('div');
    content.className = 'modal-content';
    
    const changeLogHtml = this._changeLog.length > 0
      ? '<ul>' + this._changeLog.map(log => `<li>${log}</li>`).join('') + '</ul>'
      : '<p>No changes made</p>';
    
    content.innerHTML = `
      <h3>Save Track Changes</h3>
      <div style="max-height: 200px; overflow-y: auto; margin: 10px 0;">
        ${changeLogHtml}
      </div>
      <div class="modal-buttons">
        <button id="saveTrackChangesOK" class="primary-button">Save</button>
        <button id="saveTrackChangesCancel" class="secondary-button">Cancel</button>
      </div>
    `;
    dialog.appendChild(content);

    document.body.appendChild(dialog);

    document.getElementById('saveTrackChangesOK').addEventListener('click', async () => {
      dialog.remove();
      
      if (this._onSaveFn) {
        await this._onSaveFn();
      }
      
      this._changeLog = [];
      this.deactivate();
    });

    document.getElementById('saveTrackChangesCancel').addEventListener('click', () => {
      dialog.remove();
    });

    backdrop.addEventListener('click', () => {
      dialog.remove();
    });
  }

  _updateCursor() {
    if (this._pendingAddTrack) {
      this._map.getCanvas().style.cursor = 'crosshair';
    } else {
      this._map.getCanvas().style.cursor = 'default';
    }
  }

  _handleMapClick(e) {
    if (!this._pendingAddTrack) return;

    const point = {
      lon: e.lngLat.lng,
      lat: e.lngLat.lat
    };

    this._addPointToTrack(this._pendingAddTrack, point);
  }

  _createDotElement(color) {
    const el = document.createElement('div');
    el.style.width = '12px';
    el.style.height = '12px';
    el.style.borderRadius = '50%';
    el.style.backgroundColor = color;
    el.style.border = '2px solid white';
    el.style.cursor = 'grab';
    el.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.3)';
    return el;
  }

  _createCircleElement(color) {
    const el = document.createElement('div');
    el.style.width = '40px';
    el.style.height = '40px';
    el.style.borderRadius = '50%';
    el.style.backgroundColor = color;
    el.style.border = '2px solid red';
    el.style.transform = 'translate(-50%, -50%)';
    el.style.pointerEvents = 'none';
    return el;
  }
}
